import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { deviceKeys } from '@/features/devices';
import { operatorKeys } from '@/features/operators';
import { handoverScanningApi } from './handover-scanning.api';
import { handoverScanningKeys } from './handover-scanning.queries';
import type {
  CreateAssignmentRequest,
  SelfAssignRequest,
  UpdateAssignmentRequest,
  WorkloadListParams
} from '../types';

export function useCreateAssignmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('handoverScanning');

  return useMutation({
    mutationFn: (data: CreateAssignmentRequest) =>
      handoverScanningApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.receivingLists()
      });
      queryClient.invalidateQueries({
        queryKey: deviceKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: operatorKeys.lists()
      });
    }
  });
}

export function useSelfAssignMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('handoverScanning');

  return useMutation({
    mutationFn: (data: SelfAssignRequest) =>
      handoverScanningApi.selfAssign(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('selfAssignSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.receivingLists()
      });
      queryClient.invalidateQueries({
        queryKey: deviceKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: operatorKeys.lists()
      });
    }
  });
}

export function useUpdateAssignmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('handoverScanning');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAssignmentRequest }) =>
      handoverScanningApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.details()
      });
    }
  });
}

export function useDeleteAssignmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('handoverScanning');

  return useMutation({
    mutationFn: (id: number) => handoverScanningApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.receivingLists()
      });
    }
  });
}

export function useBulkDeleteAssignmentsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('handoverScanning');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<WorkloadListParams> }
    ) => handoverScanningApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.receivingLists()
      });
    }
  });
}
